import { z } from "zod";
import { Cents, Email, Id, IsoDate, IsoDateTime, Ok } from "./common.ts";
import { endpoint } from "./endpoint.ts";
import { LegalForm } from "./company.ts";
import { COMPANY_PHASES, Dossier } from "./creation.ts";
import { QuestionKind, QuestionValue } from "./creation-questions.ts";

/**
 * Company-creation tracking (boards 64–101): the dossier state machine the
 * back-office drives (boards 102 « Machine d'états création » / 103 « Boucles
 * d'invalidation »), plus every client action of the tracking screens.
 */

// ───────────────────────── State machine ─────────────────────────

/** Board 102 codes. AE dossiers use the same codes on a shorter, 6-phase flow. */
export const TrackingState = z.enum([
  "AWAITING_CREATION_PAYMENT",
  "PRECHECK_SUBMITTED",
  "MELRYS_INFO_REQUESTED",
  "MELRYS_INFO_RECEIVED",
  "PRECHECK_APPROVED",
  "WAITING_FOR_CAPITAL_DEPOSIT",
  "CAPITAL_CERTIFICATE_REVIEW",
  "FINALIZATION_IN_PROGRESS",
  "READY_FOR_FINAL_SUBMISSION",
  "FINAL_SUBMITTED",
  "LEGAL_NOTICE_PREPARATION",
  "AWAITING_JAL_PAYMENT",
  "READY_TO_FILE",
  "FILED_WITH_INPI",
  "INPI_INFO_REQUESTED",
  "INPI_INFO_RECEIVED",
  "INPI_RESPONSE_SUBMITTED",
  "COMPANY_CREATED",
  "REJECTED_BY_MELRYS",
  "REJECTED_BY_INPI",
  "CANCELLED",
  "CLOSED_INACTIVE",
]);
export type TrackingState = z.infer<typeof TrackingState>;

/** Who has the hand at a state (board 102 « Main »). */
export const Owner = z.enum(["client", "melrys", "inpi", "done"]);
export type Owner = z.infer<typeof Owner>;

/** Auto-entrepreneur flow (boards 93–95): no capital, no legal notice. */
export const AE_PHASES = [
  "Préparation et paiement",
  "Vérification Melrys",
  "Pièces et signature",
  "Dépôt au Guichet unique",
  "Validation du Guichet unique",
  "Immatriculation",
] as const;

export const ENDED_STATES: TrackingState[] = ["COMPANY_CREATED", "REJECTED_BY_MELRYS", "REJECTED_BY_INPI", "CANCELLED", "CLOSED_INACTIVE"];

export function phasesFor(legalForm: LegalForm): readonly string[] {
  return legalForm === "AE" ? AE_PHASES : COMPANY_PHASES;
}

/** 1-based phase of a state, per flow. */
export function phaseOf(state: TrackingState, legalForm: LegalForm): number {
  const ae = legalForm === "AE";
  switch (state) {
    case "AWAITING_CREATION_PAYMENT":
      return ae ? 1 : 2;
    case "PRECHECK_SUBMITTED":
    case "MELRYS_INFO_REQUESTED":
    case "MELRYS_INFO_RECEIVED":
      return 2;
    case "PRECHECK_APPROVED":
    case "WAITING_FOR_CAPITAL_DEPOSIT":
    case "CAPITAL_CERTIFICATE_REVIEW":
      return 3;
    case "FINALIZATION_IN_PROGRESS":
    case "READY_FOR_FINAL_SUBMISSION":
    case "FINAL_SUBMITTED":
      return ae ? 3 : 4;
    case "LEGAL_NOTICE_PREPARATION":
    case "AWAITING_JAL_PAYMENT":
      return 5;
    case "READY_TO_FILE":
    case "FILED_WITH_INPI":
      return ae ? 4 : 6;
    case "INPI_INFO_REQUESTED":
    case "INPI_INFO_RECEIVED":
    case "INPI_RESPONSE_SUBMITTED":
      return ae ? 5 : 7;
    default:
      return ae ? 6 : 8;
  }
}

export function ownerOf(state: TrackingState): Owner {
  switch (state) {
    case "AWAITING_CREATION_PAYMENT":
    case "MELRYS_INFO_REQUESTED":
    case "PRECHECK_APPROVED":
    case "WAITING_FOR_CAPITAL_DEPOSIT":
    case "FINALIZATION_IN_PROGRESS":
    case "READY_FOR_FINAL_SUBMISSION":
    case "AWAITING_JAL_PAYMENT":
    case "INPI_INFO_REQUESTED":
      return "client";
    case "FILED_WITH_INPI":
    case "INPI_RESPONSE_SUBMITTED":
      return "inpi";
    case "COMPANY_CREATED":
    case "REJECTED_BY_MELRYS":
    case "REJECTED_BY_INPI":
    case "CANCELLED":
    case "CLOSED_INACTIVE":
      return "done";
    default:
      return "melrys";
  }
}

// ───────────────────────── Building blocks ─────────────────────────

export const FileRef = z.object({
  name: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedAt: IsoDateTime,
});
export type FileRef = z.infer<typeof FileRef>;

/** What the client sends for an upload (the live API takes multipart; the mock only needs metadata). */
export const UploadInput = z.object({
  name: z.string().min(1),
  sizeBytes: z.number().int().nonnegative(),
  mimeType: z.string(),
});
export type UploadInput = z.infer<typeof UploadInput>;
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_UPLOAD_TYPES = ["application/pdf", "image/jpeg", "image/png"];
export const MAX_PIECE_VERSIONS = 3;

export const PersonRole = z.enum(["sole_partner", "president", "additional_director", "partner", "entrepreneur", "host"]);
export type PersonRole = z.infer<typeof PersonRole>;

export const Person = z.object({
  id: Id,
  firstName: z.string(),
  lastName: z.string(),
  role: PersonRole,
  /** Shown after the name: « associé unique », « dirigeant supplémentaire », « Directeur général »… */
  roleLabel: z.string(),
  email: Email.nullable(),
  /** The account holder signs in-app; the others sign through a public link. */
  isAccountHolder: z.boolean(),
});
export type Person = z.infer<typeof Person>;

// ───────────────────────── Payments (Stancer) ─────────────────────────

export const PaymentKind = z.enum(["creation", "legal_notice", "registry_fees"]);
export type PaymentKind = z.infer<typeof PaymentKind>;

export const PaymentStatus = z.enum([
  "unavailable", // no amount / no Stancer link yet (quote in progress, link missing)
  "to_pay",
  "processing", // back from Stancer, waiting for reconcile
  "refused",
  "expired",
  "paid",
]);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const PaymentLine = z.object({ label: z.string(), amountCents: Cents });

export const Payment = z.object({
  kind: PaymentKind,
  status: PaymentStatus,
  /** TTC for the creation fee; exact amount (no VAT, disbursement) for legal notice and registry fees. */
  amountCents: Cents.nullable(),
  /** HT and VAT split, null for disbursements. */
  amountHtCents: Cents.nullable(),
  vatCents: Cents.nullable(),
  lines: z.array(PaymentLine),
  /** Journal or registry the amount goes to (disbursements). */
  payee: z.string().nullable(),
  /** Set once a Stancer checkout has been opened; reused on double click. */
  paymentId: z.string().nullable(),
  paidAt: IsoDateTime.nullable(),
  invoiceDocumentId: Id.nullable(),
});
export type Payment = z.infer<typeof Payment>;

// ───────────────────────── Corrections (Melrys / INPI) ─────────────────────────

/** Board 103, exact reason labels. */
export const CORRECTION_REASONS = {
  unreadable: "Document illisible",
  mismatch: "Ne correspond pas à la demande",
  wrong_info: "Information erronée",
  incomplete: "Champ incomplet",
  missing_document: "Document manquant",
  other: "Autre motif",
} as const;
export const CorrectionReason = z.enum(["unreadable", "mismatch", "wrong_info", "incomplete", "missing_document", "other"]);

/** How the client answers an item: the questionnaire input of the invalidated question (creation-questions.ts), or a document. */
export const CorrectionFieldType = QuestionKind;

/**
 * C15a (retour 3): the type of an INPI point, chosen by Melrys for each request of the INPI sent in one correction —
 * « pièce jointe » (the client uploads a file) or « précision textuelle » (a written answer).
 * 3e (réponses 04/11): the « Autre » type is removed, with no replacement; a point stored as "other" before reads as a
 * « précision textuelle » (`inpiPointKindOf`).
 */
export const InpiPointKind = z.enum(["document", "text"]);
export type InpiPointKind = z.infer<typeof InpiPointKind>;
export const INPI_POINT_KINDS: Record<InpiPointKind, string> = { document: "Pièce jointe", text: "Précision textuelle" };

/** 3e (réponses 04/11): the kind of a stored INPI point — the removed "other" reads as "text"; absent stays absent. */
export function inpiPointKindOf(kind: string | null | undefined): InpiPointKind | undefined {
  if (kind == null) return undefined;
  return kind === "document" ? "document" : "text";
}

/** C15a (retour 3): how the client answers an INPI point of each type (CorrectionItem.field). */
export const INPI_POINT_FIELDS: Record<InpiPointKind, { type: "document" | "textarea"; label: string }> = {
  document: { type: "document", label: "Document" },
  text: { type: "textarea", label: "Précision" },
};

/**
 * C15a (retour 3): « Question posée » of an INPI point — one point keeps the historic label, several are numbered
 * with their type (« Point 2 — Précision textuelle »).
 */
export function inpiPointLabel(kind: InpiPointKind, position: number, count: number): string {
  if (count > 1) return `Point ${position} — ${INPI_POINT_KINDS[kind]}`;
  return kind === "text" ? "Précision demandée par l'INPI" : "Complément demandé par l'INPI";
}

export const CorrectionAnswer = z.object({
  value: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  file: FileRef.nullable(),
  /**
   * The corrected answer in the questionnaire's own shape (address object, ISO date, option value, cents…) when the
   * item is a questionnaire question (`field.key`); `value` then holds its one-line display.
   */
  data: QuestionValue.optional(),
});
export type CorrectionAnswer = z.infer<typeof CorrectionAnswer>;

export const CorrectionItem = z.object({
  id: Id,
  section: z.string(),
  label: z.string(),
  /** « Votre réponse actuelle ». */
  currentAnswer: z.string(),
  reason: CorrectionReason,
  comment: z.string(),
  /**
   * `key`: the questionnaire question (creation-questions.ts `questionFor`) the item corrects — the client answers
   * with that question's input and rules, and the answer is written back into the dossier's questionnaire.
   * Null / absent: free item (INPI request, document, older requests).
   */
  field: z.object({ type: CorrectionFieldType, label: z.string(), options: z.array(z.string()).optional(), key: z.string().nullable().optional() }),
  /**
   * C15a (retour 3): type of an INPI point (« Pièce jointe », « Précision textuelle »). Absent: Melrys items
   * and INPI requests recorded before C15a — answered by `field.type` alone (a document).
   */
  kind: InpiPointKind.optional(),
  /** Annotated proof attached by Melrys / the INPI. */
  attachment: z.object({ title: z.string(), subtitle: z.string(), note: z.string().nullable() }).nullable(),
  status: z.enum(["to_fix", "fixed"]),
  answer: CorrectionAnswer.nullable(),
  /**
   * « Correction refusée »: the answer the client had sent for this point, refused by Melrys at the last review (the
   * questionnaire is back to its previous value, shown as « Votre réponse actuelle »). Null / absent otherwise.
   */
  refused: z.object({ answer: z.string(), at: IsoDateTime }).nullable().optional(),
  /** Versions already uploaded for a document item (3 max). */
  versions: z.number().int(),
  /** #39: the client sent back the same value as « Votre réponse actuelle » (flagged to Melrys on the line). */
  unchanged: z.boolean().optional(),
  history: z.array(z.object({ at: IsoDateTime, text: z.string() })),
});
export type CorrectionItem = z.infer<typeof CorrectionItem>;

export const CorrectionRequest = z.object({
  id: Id,
  source: z.enum(["melrys", "inpi"]),
  status: z.enum(["open", "sent"]),
  dueDate: IsoDate,
  createdAt: IsoDateTime,
  sentAt: IsoDateTime.nullable(),
  items: z.array(CorrectionItem),
  /** #38: the message the client sent with the answers, for the person handling the dossier. */
  clientComment: z.string().nullable().optional(),
});
export type CorrectionRequest = z.infer<typeof CorrectionRequest>;

/**
 * A questionnaire answer rewritten by a correction (back-office only): the question, its previous and corrected value
 * (questionnaire shape + one-line display), who answered and when, and Melrys' review of the change.
 */
export const AnswerChange = z.object({
  id: Id,
  /** Question key (creation-questions.ts), = the back-office row id. */
  key: z.string(),
  label: z.string(),
  section: z.string(),
  previous: QuestionValue,
  previousLabel: z.string(),
  corrected: QuestionValue,
  correctedLabel: z.string(),
  source: z.enum(["melrys", "inpi"]),
  requestId: Id,
  itemId: Id,
  /** « Prénom Nom (client) ». */
  by: z.string(),
  at: IsoDateTime,
  review: z.enum(["pending", "accepted", "refused"]),
  reviewedBy: z.string().nullable(),
  reviewedAt: IsoDateTime.nullable(),
});
export type AnswerChange = z.infer<typeof AnswerChange>;

// ───────────────────────── Signatures (BoldSign) ─────────────────────────

export const Invitation = z.object({
  id: Id,
  email: Email,
  sentAt: IsoDateTime,
  openedAt: IsoDateTime.nullable(),
  expiresAt: IsoDateTime,
  status: z.enum(["sent", "opened", "signed", "expired", "replaced"]),
  signedAt: IsoDateTime.nullable(),
});
export type Invitation = z.infer<typeof Invitation>;

export const Signer = z.object({
  personId: Id,
  name: z.string(),
  roleLabel: z.string(),
  isAccountHolder: z.boolean(),
  status: z.enum(["pending", "signed"]),
  signedAt: IsoDateTime.nullable(),
  invitations: z.array(Invitation),
});
export type Signer = z.infer<typeof Signer>;

export const SignableKind = z.enum(["statuts_draft", "statuts", "subscribers_list", "power_of_attorney", "non_conviction", "domiciliation_attestation"]);
export type SignableKind = z.infer<typeof SignableKind>;

export const SignableDocument = z.object({
  id: Id,
  kind: SignableKind,
  title: z.string(),
  description: z.string(),
  pages: z.number().int(),
  status: z.enum(["to_sign", "partially_signed", "signed"]),
  signedAt: IsoDateTime.nullable(),
  /** Non-conviction declarations need the signer's parents' names (art. A.123-51). */
  requiresFiliation: z.boolean(),
  signers: z.array(Signer),
});
export type SignableDocument = z.infer<typeof SignableDocument>;

export const SignatureInput = z.object({
  mode: z.enum(["typed", "drawn"]),
  /** Typed: the full name. Drawn: the signer's name for the audit trail. */
  name: z.string().trim().min(3, { message: "Saisissez votre nom complet." }).max(120),
  style: z.enum(["classic", "elegant", "handwritten", "natural"]).nullable(),
  /** Drawn signature as a PNG data URL. */
  image: z.string().nullable(),
});
export type SignatureInput = z.infer<typeof SignatureInput>;

export const Filiation = z.object({
  father: z.string().trim().min(3, { message: "Renseignez le nom et prénom de chacun de vos parents (3 à 200 caractères) avant de signer." }).max(200),
  mother: z.string().trim().min(3, { message: "Renseignez le nom et prénom de chacun de vos parents (3 à 200 caractères) avant de signer." }).max(200),
});

// ───────────────────────── Capital ─────────────────────────

export const Capital = z.object({
  status: z.enum(["waiting", "review", "refused", "accepted"]),
  totalCents: Cents,
  /** Legal minimum: SASU 50 %, EURL 20 % of the subscribed capital. */
  minimumCents: Cents,
  /** What the client deposits (C12a): the paid-in amount entered in the questionnaire (as in the statuts), else the subscribed capital. */
  toDepositCents: Cents,
  minimumPercent: z.number().int(),
  depositedCents: Cents.nullable(),
  depositDate: IsoDate.nullable(),
  bank: z.string().nullable(),
  certificate: FileRef.nullable(),
  versions: z.number().int(),
  waitingSince: IsoDate,
  refusalReason: z.string().nullable(),
});
export type Capital = z.infer<typeof Capital>;

// ───────────────────────── Final documents ─────────────────────────

export const IdChoice = z.enum(["id_card", "passport", "residence_permit", "none"]);
export type IdChoice = z.infer<typeof IdChoice>;
export const SUBSTITUTE_DOCS = ["Extrait d’acte de naissance", "Livret de famille", "Carte d’ancien combattant", "Carte d’invalide de guerre", "Carte d’invalide civil"] as const;

export const PieceStatus = z.enum(["to_upload", "uploaded", "validated", "to_fix", "provided"]);
export type PieceStatus = z.infer<typeof PieceStatus>;

export const Piece = z.object({
  id: Id,
  /** Identity pieces are tied to a person. */
  personId: Id.nullable(),
  title: z.string(),
  description: z.string(),
  status: PieceStatus,
  file: FileRef.nullable(),
  versions: z.number().int(),
  refusal: z.string().nullable(),
  pages: z.number().int(),
});
export type Piece = z.infer<typeof Piece>;

export const IdentityEntry = z.object({
  personId: Id,
  choice: IdChoice.nullable(),
  /** « Je n'ai ni l'un, ni l'autre » → substitute document. */
  substitute: z.string().nullable(),
  /** Non-EU nationals also need a residence permit and a tax residence proof. */
  nonEu: z.boolean(),
  pieceId: Id,
});
export type IdentityEntry = z.infer<typeof IdentityEntry>;

export const Consents = z.object({
  accuracy: z.boolean(),
  mandate: z.boolean(),
  sireneOptOut: z.boolean(),
  prospecting: z.boolean(),
});
export type Consents = z.infer<typeof Consents>;

export const Finalization = z.object({
  identity: z.array(IdentityEntry),
  pieces: z.array(Piece),
  consents: Consents,
  /** Exactly what still blocks « Valider et transmettre », in product wording. */
  missing: z.array(z.string()),
  submittedAt: IsoDateTime.nullable(),
  /**
   * Board 93 (AE): once everything is signed and complete, the two confirmations are ticked automatically until the
   * client changes them (« Cases cochées automatiquement… Vous pouvez les décocher. »). True while that applies.
   */
  consentsAuto: z.boolean().optional(),
});
export type Finalization = z.infer<typeof Finalization>;

// ───────────────────────── Legal notice, INPI, company, ending ─────────────────────────

export const LegalNotice = z.object({
  status: z.enum(["documents_review", "ready", "quote", "awaiting_payment", "paid", "published"]),
  journal: z.string().nullable(),
  reference: z.string().nullable(),
  publishedAt: IsoDate.nullable(),
});
export type LegalNotice = z.infer<typeof LegalNotice>;

export const Inpi = z.object({
  liasse: z.string().nullable(),
  filedAt: IsoDate.nullable(),
  decisionAt: IsoDate.nullable(),
  /** The INPI validated without a request: phase 7 is shown « (sautée) ». */
  verificationSkipped: z.boolean(),
});

export const RegisteredCompany = z.object({
  siren: z.string().regex(/^\d{9}$/),
  siret: z.string().regex(/^\d{14}$/),
  registeredAt: IsoDate,
  ape: z.string(),
  address: z.string(),
  // ── registered identity (« Enregistrer l'immatriculation », additive: absent on records made before) ──
  /** Dénomination (company) or « Prénom Nom » (EI / AE) as registered. */
  name: z.string().optional(),
  /** « SASU », « SAS », « EURL », « SARL », « EI », « AE » (contracts/registry.ts REGISTRY_LEGAL_FORMS). */
  legalForm: z.string().optional(),
  /** Share capital as registered (null: EI / AE). */
  capitalCents: Cents.nullable().optional(),
  /** Official NAF rév. 2 label of `ape`. */
  apeLabel: z.string().optional(),
  /** Where the identity came from: a registry (INSEE Sirene, Data&Key, dev simulation) or the agent's manual entry. */
  source: z.enum(["insee", "datakey", "simulation", "manual"]).optional(),
});

export const Refund = z.object({
  percent: z.number().int(),
  amountCents: Cents,
  htCents: Cents,
  vatCents: Cents,
  /** Registry fees: 100 % before INPI filing, 0 % after. */
  registryFeesCents: Cents,
  status: z.enum(["none", "pending", "issued"]),
  issuedAt: IsoDate.nullable(),
  creditNoteNumber: z.string().nullable(),
});
export type Refund = z.infer<typeof Refund>;

export const Ending = z.object({
  kind: z.enum(["cancelled", "rejected_by_melrys", "rejected_by_inpi", "closed_inactive"]),
  at: IsoDate,
  reason: z.string().nullable(),
  refund: Refund.nullable(),
});
export type Ending = z.infer<typeof Ending>;

// ───────────────────────── Tracking aggregate ─────────────────────────

/** Creation advisor following the dossier (« Sarah Lemaire · votre conseillère création »), and the number to call. */
export const TrackingAdvisor = z.object({
  name: z.string(),
  initials: z.string(),
  /** « votre conseillère création » */
  role: z.string(),
  /** Display format, « +33 1 76 43 12 34 ». */
  phone: z.string(),
});
export type TrackingAdvisor = z.infer<typeof TrackingAdvisor>;

export const Tracking = z.object({
  dossier: Dossier,
  state: TrackingState,
  owner: Owner,
  /** 1-based, and total phases for this flow (8 company, 6 AE). */
  phase: z.number().int(),
  phaseCount: z.number().int(),
  submittedAt: IsoDateTime,
  updatedAt: IsoDateTime,
  people: z.array(Person),
  address: z.string(),
  /** Day + month of the first closing, chosen in the forms (« 31/12 »). */
  closingDayMonth: z.string(),
  payments: z.object({ creation: Payment, legalNotice: Payment.nullable(), registryFees: Payment.nullable() }),
  correction: CorrectionRequest.nullable(),
  statutsDraft: SignableDocument.nullable(),
  capital: Capital.nullable(),
  activityStartDate: IsoDate.nullable(),
  finalization: Finalization,
  /** Documents to sign at finalisation (statuts, subscribers, power of attorney, declarations…). */
  signatures: z.array(SignableDocument),
  legalNotice: LegalNotice.nullable(),
  inpi: Inpi,
  company: RegisteredCompany.nullable(),
  ending: Ending.nullable(),
  unreadMessages: z.number().int(),
  advisor: TrackingAdvisor,
  /**
   * Registry fees (RCS + beneficial owners, regulated tariff, disbursement) the company pays by card before
   * the INPI filing, known before that payment opens (« 58,86 € pour une société commerciale »). Null for AE.
   */
  registryFeesCents: Cents.nullable(),
});
export type Tracking = z.infer<typeof Tracking>;

// ───────────────────────── Documents ─────────────────────────

export const DocCategory = z.enum(["signed", "piece", "invoice", "official"]);
export type DocCategory = z.infer<typeof DocCategory>;

export const DocStatus = z.enum(["to_sign", "signed", "validated", "uploaded", "to_fix", "in_review", "available", "issued", "pending_signature", "generated"]);
export type DocStatus = z.infer<typeof DocStatus>;

export const DossierDocument = z.object({
  id: Id,
  title: z.string(),
  category: DocCategory,
  /** Dossier step it belongs to (« Paiement de la création », « Dépôt du capital »…), for grouping. */
  step: z.string(),
  status: DocStatus,
  issuer: z.string().nullable(),
  date: IsoDate,
  pages: z.number().int(),
  format: z.enum(["pdf", "jpeg"]),
  /** Invoice / credit-note number and amount: `amountCents` is the total TTC, split into HT + VAT (VAT 0 for disbursements). */
  number: z.string().nullable(),
  amountCents: Cents.nullable(),
  amountHtCents: Cents.nullable(),
  vatCents: Cents.nullable(),
  /** Credit note: the invoice it cancels (« Facture liée : FAC-2026-0412 du 11/09/2026 »). */
  linkedInvoice: z.object({ number: z.string(), date: IsoDate }).nullable(),
  /** Template the preview renders. */
  template: z.enum(["invoice", "credit_note", "statuts", "id_card", "domiciliation", "kbis", "sirene", "inpi_receipt", "legal_notice", "generic"]),
  /** For signable documents: id to open the signature window. */
  signableId: Id.nullable(),
});
export type DossierDocument = z.infer<typeof DossierDocument>;

/**
 * PDF of a generated document (statuts, subscribers list, procuration, declarations, host attestation): a short-lived
 * URL. `signed`: every signer signed — the PDF carries the signatures and the « Attestation de signature électronique »
 * page; otherwise the unsigned version (the one presented at signature once someone signed). `sha256` of the PDF served.
 */
export const GeneratedDocumentFile = z.object({ url: z.string(), fileName: z.string(), mimeType: z.string(), signed: z.boolean(), sha256: z.string().nullable() });
export type GeneratedDocumentFile = z.infer<typeof GeneratedDocumentFile>;

// ───────────────────────── Support chat & bookings ─────────────────────────

export const ChatAttachment = z.object({ name: z.string(), sizeBytes: z.number().int(), converted: z.boolean() });
export const ChatMessage = z.object({
  id: Id,
  author: z.enum(["client", "agent", "system"]),
  authorName: z.string().nullable(),
  text: z.string(),
  attachments: z.array(ChatAttachment),
  sentAt: IsoDateTime,
  readAt: IsoDateTime.nullable(),
  /** System notices can link to the correction window. */
  action: z.enum(["open_correction"]).nullable(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

export const BookingSlot = z.object({ id: Id, start: IsoDateTime, available: z.boolean() });
export const Booking = z.object({ id: Id, start: IsoDateTime, channel: z.enum(["meet", "phone"]), advisor: z.string() });

// ───────────────────────── Cancellation ─────────────────────────

export const CANCEL_REASONS = [
  "Mon projet a changé",
  "Je choisis une autre forme juridique",
  "Je passe par un autre prestataire",
  "C'est plus long que prévu",
  "Le coût est trop élevé",
  "Je reporte ma création",
  "Autre raison",
] as const;

export const CancellationQuote = z.object({
  /** 100 / 50 / 25 / 0 % tier of the creation fees, by progress. */
  tier: z.union([z.literal(100), z.literal(50), z.literal(25), z.literal(0)]),
  /** Creation fees actually collected (0 before payment). */
  collectedCents: Cents,
  feeTtcCents: Cents,
  refund: Refund,
  /** Amount the tier will give once the payment is confirmed (when nothing is collected yet): TTC, and its HT + VAT split. */
  refundIfPaidCents: Cents,
  refundIfPaidHtCents: Cents,
  refundIfPaidVatCents: Cents,
});
export type CancellationQuote = z.infer<typeof CancellationQuote>;

// ───────────────────────── Public signature pages (100, 101) ─────────────────────────

export const PublicSignature = z.object({
  kind: z.enum(["cosigner", "host"]),
  status: z.enum(["pending", "signed", "expired", "replaced", "used"]),
  companyName: z.string(),
  legalForm: LegalForm,
  signerName: z.string(),
  /** « Directeur général », « tiers-hébergeur du siège ». */
  roleLabel: z.string(),
  invitedBy: z.string(),
  /** Host: address of the premises lent as the registered office (« 12 rue Nationale, 59000 Lille »); null for co-signers. */
  premisesAddress: z.string().nullable(),
  expiresAt: IsoDateTime,
  consents: z.object({ cgu: z.boolean(), dpa: z.boolean() }),
  documents: z.array(z.object({ id: Id, title: z.string(), kind: SignableKind, signed: z.boolean() })),
  requiresFiliation: z.boolean(),
});
export type PublicSignature = z.infer<typeof PublicSignature>;

// ───────────────────────── Endpoints ─────────────────────────

const D = z.object({ dossierId: Id });
const Base = "/creation/dossiers/:dossierId";

export const trackingApi = {
  get: endpoint({ id: "tracking.get", method: "GET", path: `${Base}/tracking`, params: D, response: Tracking }),
  documents: endpoint({ id: "tracking.documents", method: "GET", path: `${Base}/documents`, params: D, response: z.object({ items: z.array(DossierDocument) }) }),
  /**
   * « Aperçu » / « Télécharger » of a document: a generated document (`DossierDocument.signableId` set; id `doc_…` or the
   * signable id) or the client's own uploaded piece (`doc_capital`, `doc_pc_…`: PDF or image, `signed` false, `sha256`
   * null). Append `&inline=1` to `url` to display it (iframe / img) instead of downloading. 404 for other documents.
   */
  documentFile: endpoint({
    id: "tracking.documents.file", method: "GET", path: `${Base}/documents/:documentId/file`,
    params: D.extend({ documentId: Id }),
    response: GeneratedDocumentFile,
  }),

  // Stancer payments (creation fee, legal notice, registry fees)
  checkout: endpoint({
    id: "tracking.payments.checkout", method: "POST", path: `${Base}/payments/:kind/checkout`,
    params: D.extend({ kind: PaymentKind }),
    response: z.object({ paymentId: z.string(), checkoutUrl: z.string() }),
  }),
  reconcile: endpoint({
    id: "tracking.payments.reconcile", method: "POST", path: `${Base}/payments/:kind/reconcile`,
    params: D.extend({ kind: PaymentKind }), response: z.object({ status: PaymentStatus }),
  }),
  /** « Le paiement n'a pas abouti ? Reprendre le paiement. » — clears the wait. */
  abandonPayment: endpoint({
    id: "tracking.payments.abandon", method: "POST", path: `${Base}/payments/:kind/abandon`,
    params: D.extend({ kind: PaymentKind }), response: z.object({ status: PaymentStatus }),
  }),

  // Corrections (Melrys and INPI requests share the loop, board 103)
  saveCorrection: endpoint({
    id: "tracking.corrections.save", method: "PUT", path: `${Base}/corrections/:requestId/items/:itemId`,
    params: D.extend({ requestId: Id, itemId: Id }),
    /** `data`: the corrected answer of a questionnaire item (`field.key`), in the questionnaire's shape; `value` is then ignored. */
    body: z.object({ value: z.string().max(15000).nullable(), postalCode: z.string().nullable(), city: z.string().nullable(), file: UploadInput.nullable(), data: QuestionValue.optional() }),
    response: CorrectionItem,
  }),
  /**
   * The annotated proof attached to a correction item (`attachment`), as a file to display inline:
   * a short-lived URL (pre-signed in the API) with its type and page count. 404 when the item has no attachment.
   */
  correctionAttachment: endpoint({
    id: "tracking.corrections.attachment", method: "GET", path: `${Base}/corrections/:requestId/items/:itemId/attachment`,
    params: D.extend({ requestId: Id, itemId: Id }),
    response: z.object({ url: z.string(), fileName: z.string(), mimeType: z.string(), pageCount: z.number().int() }),
  }),
  submitCorrections: endpoint({
    id: "tracking.corrections.submit", method: "POST", path: `${Base}/corrections/:requestId/submit`,
    params: D.extend({ requestId: Id }),
    /** Optional message for the person handling the dossier (#38). */
    body: z.object({ comment: z.string().trim().max(1000, "1000 caractères maximum.").optional() }).optional(),
    response: z.object({ sent: z.number().int() }),
  }),

  // Signatures
  sign: endpoint({
    id: "tracking.signatures.sign", method: "POST", path: `${Base}/signatures/:documentId/sign`,
    params: D.extend({ documentId: Id }),
    body: z.object({ signature: SignatureInput, filiation: Filiation.nullable() }),
    response: SignableDocument,
  }),
  invite: endpoint({
    id: "tracking.signatures.invite", method: "POST", path: `${Base}/signatures/:documentId/invitations`,
    params: D.extend({ documentId: Id }),
    body: z.object({ personId: Id, email: Email }),
    response: z.object({ invitation: Invitation, document: SignableDocument }),
  }),

  // Capital
  submitCapital: endpoint({
    id: "tracking.capital.submit", method: "POST", path: `${Base}/capital/certificate`,
    params: D,
    body: z.object({ file: UploadInput.nullable(), totalCents: Cents, depositedCents: Cents, depositDate: IsoDate }),
    response: Capital,
  }),

  // Finalisation
  setStartDate: endpoint({
    id: "tracking.activityStartDate.set", method: "PUT", path: `${Base}/activity-start-date`,
    params: D, body: z.object({ date: IsoDate }), response: z.object({ activityStartDate: IsoDate, firstClosingDate: IsoDate.nullable() }),
  }),
  setIdentity: endpoint({
    id: "tracking.identity.set", method: "PUT", path: `${Base}/identity/:personId`,
    params: D.extend({ personId: Id }), body: z.object({ choice: IdChoice, substitute: z.string().nullable() }), response: IdentityEntry,
  }),
  uploadPiece: endpoint({
    id: "tracking.pieces.upload", method: "POST", path: `${Base}/pieces/:pieceId/versions`,
    params: D.extend({ pieceId: Id }), body: UploadInput, response: Piece,
  }),
  setConsents: endpoint({ id: "tracking.consents.set", method: "PUT", path: `${Base}/consents`, params: D, body: Consents, response: Consents }),
  submitFinal: endpoint({ id: "tracking.final.submit", method: "POST", path: `${Base}/final-submission`, params: D, response: Ok }),

  // Legal notice
  startLegalNotice: endpoint({ id: "tracking.legalNotice.start", method: "POST", path: `${Base}/legal-notice/start`, params: D, response: Ok }),
  confirmPublication: endpoint({ id: "tracking.legalNotice.continue", method: "POST", path: `${Base}/legal-notice/continue`, params: D, response: Ok }),

  // Cancellation (board 96)
  cancellationQuote: endpoint({ id: "tracking.cancellation.quote", method: "GET", path: `${Base}/cancellation`, params: D, response: CancellationQuote }),
  cancel: endpoint({
    id: "tracking.cancellation.confirm", method: "POST", path: `${Base}/cancellation`, params: D,
    body: z.object({ reason: z.enum(CANCEL_REASONS), details: z.string().max(2000).nullable() }),
    response: Ending,
  }),

  // Support (boards 69 / 69b) and bookings
  messages: endpoint({ id: "tracking.support.messages", method: "GET", path: `${Base}/support/messages`, params: D, response: z.object({ items: z.array(ChatMessage), agentsOnline: z.boolean() }) }),
  sendMessage: endpoint({
    id: "tracking.support.send", method: "POST", path: `${Base}/support/messages`, params: D,
    body: z.object({ text: z.string().max(5000), attachments: z.array(UploadInput).max(5) }), response: ChatMessage,
  }),
  markRead: endpoint({ id: "tracking.support.read", method: "POST", path: `${Base}/support/read`, params: D, response: Ok }),
  slots: endpoint({ id: "tracking.bookings.slots", method: "GET", path: "/creation/bookings/slots", query: z.object({ purpose: z.literal("creation") }), response: z.object({ advisor: z.string(), slots: z.array(BookingSlot) }) }),
  book: endpoint({
    id: "tracking.bookings.create", method: "POST", path: "/creation/bookings",
    body: z.object({ slotId: Id, channel: z.enum(["meet", "phone"]), purpose: z.literal("creation") }), response: Booking,
  }),
};

/** Public signature pages: authenticated by the token in the URL (no session). */
export const publicSignatureApi = {
  get: endpoint({ id: "publicSignature.get", method: "GET", path: "/public/signatures/:token", params: z.object({ token: z.string() }), response: PublicSignature, auth: false }),
  consent: endpoint({
    id: "publicSignature.consent", method: "POST", path: "/public/signatures/:token/consents", params: z.object({ token: z.string() }),
    body: z.object({ cgu: z.boolean(), dpa: z.boolean() }), response: PublicSignature, auth: false,
  }),
  sign: endpoint({
    id: "publicSignature.sign", method: "POST", path: "/public/signatures/:token/sign", params: z.object({ token: z.string() }),
    body: z.object({ signature: SignatureInput, filiation: Filiation.nullable(), hostQuality: z.enum(["owner", "tenant"]).nullable() }),
    response: PublicSignature, auth: false,
  }),
  /** « Lire le document » before signing: the PDF of one of the link's documents (signed version once complete). */
  documentFile: endpoint({
    id: "publicSignature.documentFile", method: "GET", path: "/public/signatures/:token/documents/:documentId/file",
    params: z.object({ token: z.string(), documentId: Id }), response: GeneratedDocumentFile, auth: false,
  }),
};

/**
 * Mock-only: the external Stancer hosted page and the back-office actions
 * (validation, corrections, publication…) can't be triggered by the client.
 * These never exist on the live API.
 */
export const trackingSandboxApi = {
  stancerPay: endpoint({
    id: "sandbox.stancer.creationPayment", method: "POST", path: "/sandbox/stancer/payments/:paymentId",
    params: z.object({ paymentId: z.string() }), body: z.object({ outcome: z.enum(["success", "refused", "cancel"]) }), response: Ok,
  }),
  scenarios: endpoint({
    id: "sandbox.tracking.scenarios", method: "GET", path: "/sandbox/creation/dossiers/:dossierId/scenarios", params: D,
    response: z.object({ current: z.string().nullable(), items: z.array(z.object({ id: z.string(), board: z.string(), group: z.string(), label: z.string() })) }),
  }),
  applyScenario: endpoint({
    id: "sandbox.tracking.apply", method: "POST", path: "/sandbox/creation/dossiers/:dossierId/scenarios/:scenarioId",
    params: D.extend({ scenarioId: z.string() }), response: Ok,
  }),
};
